import type {IssueFieldMultiSelectOption} from '@github-ui/item-picker/IssueMultiSelectFieldPicker'
import {ConfirmationDialog} from '@primer/react'
import {Fragment} from 'react'

import {stripTimezone} from '../../utils/date-formatting'
import type {FieldsSectionFragment$data} from './__generated__/FieldsSectionFragment.graphql'
import {LABELS} from './constants/labels'
import {IssueFieldDateEditor} from './IssueFieldDateEditor'
import {IssueFieldMultiSelectEditor} from './IssueFieldMultiSelectEditor'
import {IssueFieldNumberEditor} from './IssueFieldNumberEditor'
import {IssueFieldSingleSelectEditor} from './IssueFieldSingleSelectEditor'
import {IssueFieldTextEditor} from './IssueFieldTextEditor'

type FieldValue = NonNullable<NonNullable<NonNullable<FieldsSectionFragment$data['issueFieldValues']>['nodes']>[number]>
type FieldValueField = NonNullable<FieldValue['field']>
type FieldValueValues = Omit<FieldValue, 'field' | 'value'>

// Date is included because DatePicker returns Date objects during issue creation,
// while the backend returns ISO date strings
export type SafeFieldValue = FieldValue & {
  field: FieldValueField & {id: string}
  value: string | Date | FieldValueValues
}

type EditorProps<T> = {
  fieldValue: SafeFieldValue
  readonly: boolean
  onCommit: IssueFieldOnCommitFn<T>
  className?: string
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

export type IssueFieldOnCommitFn<T> = (fieldId: string, value: T) => void

export type IssueFieldEditorsProps<T> = {
  errorPresent: Record<string, boolean>
  setErrorPresent: (fieldId: string, present: boolean) => void
} & EditorProps<T>

export function getIssueFieldEditor<T>({
  errorPresent,
  setErrorPresent,
  fieldValue,
  ...props
}: IssueFieldEditorsProps<T>) {
  if (!fieldValue.field) return null

  const fieldId = fieldValue.field.id
  // Only show error dialog for single and multi select fields
  const showErrorDialog = fieldValue.field.dataType === 'SINGLE_SELECT' || fieldValue.field.dataType === 'MULTI_SELECT'
  const editor = getEditor({fieldValue, ...props}, errorPresent[fieldId])

  return (
    <Fragment key={fieldValue.field.name}>
      {editor}
      {showErrorDialog && errorPresent[fieldId] && (
        <ErrorDialog onClose={() => setErrorPresent(fieldId, false)} message={LABELS.mutationErrorMessage} />
      )}
    </Fragment>
  )
}

export function getEditor<T>(
  {fieldValue, readonly, onCommit, className, showVisibilityIndicator, isOwnerEnterpriseManaged}: EditorProps<T>,
  errorPresent?: boolean,
) {
  const isCleared = fieldValue.value === ''

  switch (fieldValue.field.dataType) {
    case 'TEXT':
      return (
        <IssueFieldTextEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={fieldValue.value || ''}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          errorPresent={errorPresent}
          className={className}
          showVisibilityIndicator={showVisibilityIndicator}
          isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
        />
      )
    case 'SINGLE_SELECT':
      return (
        <IssueFieldSingleSelectEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={{
            name: isCleared || !fieldValue?.name ? '' : fieldValue.name,
            color: isCleared || !fieldValue?.color ? '' : fieldValue.color,
            description: isCleared || !fieldValue?.description ? '' : fieldValue.description,
          }}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          className={className}
          errorPresent={errorPresent}
          showVisibilityIndicator={showVisibilityIndicator}
          isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
        />
      )
    case 'DATE': {
      // fieldValue.value can be a Date object (during issue creation) or a string (from backend)
      let dateValue: Date | null = null
      const rawValue = fieldValue.value as Date | string | null
      if (rawValue) {
        if (rawValue instanceof Date) {
          dateValue = rawValue
        } else if (typeof rawValue === 'string') {
          dateValue = stripTimezone(rawValue)
        }
      }
      return (
        <IssueFieldDateEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          value={dateValue}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          errorPresent={errorPresent}
          className={className}
          showVisibilityIndicator={showVisibilityIndicator}
          isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
        />
      )
    }
    case 'NUMBER': {
      // Display empty string for number fields without values so the text editor shows the styled 'None yet' placeholder instead of defaulting to '0'
      // Handle both numberValue (from GraphQL/database) and value (from component state during creation)
      const hasNumberValue = fieldValue.numberValue !== undefined && fieldValue.numberValue !== null
      const hasValue = fieldValue.value !== undefined && fieldValue.value !== null && fieldValue.value !== ''

      const numberValue = hasNumberValue ? fieldValue.numberValue.toString() : hasValue ? String(fieldValue.value) : ''

      return (
        <IssueFieldNumberEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          // Pass empty string so text editor shows styled "None yet" when no number is set
          initialValue={numberValue}
          readonly={readonly}
          onCommit={(fieldId, value) => {
            // For when the field is cleared, pass empty string to allow proper clearing logic. Otherwise Number("") returns 0, which we don't want
            if (value === '') {
              onCommit(fieldId, value as T)
            } else {
              const numericValue = Number(value)
              onCommit(fieldId, numericValue as T)
            }
          }}
          errorPresent={errorPresent}
          className={className}
          showVisibilityIndicator={showVisibilityIndicator}
          isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
        />
      )
    }
    case 'MULTI_SELECT': {
      const selectedOptions: IssueFieldMultiSelectOption[] = isCleared
        ? []
        : (fieldValue.options ?? []).map(option => ({
            id: option.id,
            name: option.name,
            description: option.description,
            color: option.color as IssueFieldMultiSelectOption['color'],
          }))
      return (
        <IssueFieldMultiSelectEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={selectedOptions.length > 0 ? selectedOptions : null}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          className={className}
          errorPresent={errorPresent}
          showVisibilityIndicator={showVisibilityIndicator}
          isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
        />
      )
    }
    default:
      throw new Error(`Unsupported field type: ${fieldValue.field.dataType}`)
  }
}

// Generic error dialog
export function ErrorDialog({onClose, message}: {onClose: () => void; message: string}) {
  return (
    <ConfirmationDialog title="Error" confirmButtonContent="OK" confirmButtonType="primary" onClose={onClose}>
      {message}
    </ConfirmationDialog>
  )
}
